/**
 * The walking monsters (05 §6.1) in their arcade look (the owner's choice, 2026-09-23; drawn
 * from the arcade's own round screenshots): Zen-chan, Mighta, Hidegons, Drunk, Banebou. Each is
 * a puppet of a few rigid parts, about two tiles tall like the 16x16 sprite, facing +x.
 * Poses may move: body, footNear, footFar, and the named extra part (key, rock, bottle, spring).
 */

import { type Material, Matrix4 } from 'three';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { Palette } from '../palette';
import { bodyRamp, eyes } from './parts';
import { Puppet } from './puppet';

function feet(pup: Puppet, p: Palette, ramp: string, spread = 0.3, size = 0.26): void {
  for (const [name, z] of [['footNear', spread], ['footFar', -spread]] as const) {
    const mb = new MeshBuilder();
    mb.blob(size, size * 0.5, size * 0.72, p.color(`${ramp}.1`), place(0.06, 0, 0), {
      topColor: p.color(`${ramp}.2`), flatBottom: true, detail: 2,
    });
    pup.add(name, mb, 0.02, 0.1, z);
  }
}

/** A tall black slit of an eye, as Zen-chan, Pulpul and the Invader have. */
export function slit(mb: MeshBuilder, p: Palette, x: number, y: number, z: number, h = 0.2): void {
  mb.blob(0.05, h, 0.06, p.color('eye.0'), place(x, y, z), { detail: 1 });
}

/** Zen-chan: a lavender wind-up robot, a dome of a head-body with slit eyes and a key in its back. */
export function buildZenChan(p: Palette, angry: boolean, material: Material): Puppet {
  const r = bodyRamp('zen', angry);
  const pup = new Puppet(material);
  pup.turn = 0.7;
  const body = new MeshBuilder();
  body.blob(0.64, 0.6, 0.6, p.color(`${r}.2`), place(0, 0.62, 0), { detail: 3 });
  body.cylinder(0.62, 0.64, 0.26, 20, p.color(`${r}.1`), place(0, 0.14, 0), p.color(`${r}.0`));
  slit(body, p, 0.63, 0.72, 0.15, 0.22);
  slit(body, p, 0.63, 0.72, -0.15, 0.22);
  pup.add('body', body, 0, 0.06, 0);
  // The wind-up key: a blue bow with a purple boss, on a short stem.
  const key = new MeshBuilder();
  key.cylinder(0.06, 0.06, 0.22, 8, p.color('metal.2'), new Matrix4().makeRotationZ(Math.PI / 2).multiply(place(0, -0.2, 0)));
  key.blob(0.08, 0.3, 0.12, p.color('windKey.2'), place(-0.22, 0.12, 0), { detail: 2 });
  key.blob(0.08, 0.3, 0.12, p.color('windKey.2'), place(-0.22, -0.12, 0), { detail: 2 });
  key.blob(0.1, 0.12, 0.12, p.color('windKey.1'), place(-0.22, 0, 0), { detail: 2 });
  pup.add('key', key, -0.6, 0.7, 0, 'body');
  feet(pup, p, 'hotFeet');
  return pup;
}

/** Mighta: a white hooded robe, a dark face with pink eyes, blue shoes; throws rocks. */
export function buildMighta(p: Palette, angry: boolean, material: Material): Puppet {
  const r = bodyRamp('robe', angry);
  const pup = new Puppet(material);
  pup.turn = 0.7;
  const body = new MeshBuilder();
  body.cylinder(0.62, 0.36, 1.05, 16, p.color(`${r}.2`), place(0, 0.1, 0), p.color(`${r}.1`));
  body.blob(0.56, 0.52, 0.54, p.color(`${r}.2`), place(0.02, 1.2, 0), { detail: 3 });
  // The hood's peak, flopping back.
  body.blob(0.18, 0.34, 0.16, p.color(`${r}.2`), new Matrix4().makeTranslation(-0.36, 1.62, 0)
    .multiply(new Matrix4().makeRotationZ(0.9)), { detail: 2 });
  body.blob(0.26, 0.34, 0.38, p.color('ink.1'), place(0.4, 1.14, 0), { detail: 2 });
  body.blob(0.06, 0.08, 0.07, p.color('cheek.1'), place(0.62, 1.18, 0.14), { detail: 1 });
  body.blob(0.06, 0.08, 0.07, p.color('cheek.1'), place(0.62, 1.18, -0.14), { detail: 1 });
  pup.add('body', body, 0, 0.04, 0);
  const rock = new MeshBuilder();
  rock.blob(0.34, 0.3, 0.32, p.color('rock.1'), place(0, 0, 0), { topColor: p.color('rock.2'), detail: 0, smooth: false });
  const held = pup.add('rock', rock, 0.72, 0.85, 0.3, 'body');
  held.visible = false;
  feet(pup, p, 'shoe', 0.24, 0.22);
  return pup;
}

/** Hidegons: a shaggy lavender beast with two feelers, one big eye, a pink mouth; breathes fire. */
export function buildHidegons(p: Palette, angry: boolean, material: Material): Puppet {
  const r = bodyRamp('hidegons', angry);
  const pup = new Puppet(material);
  pup.turn = 0.7;
  const body = new MeshBuilder();
  body.blob(0.64, 0.64, 0.6, p.color(`${r}.2`), place(0, 0.74, 0), { detail: 3 });
  // A shaggy fringe round the bottom and a tuft on top of the head.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    body.blob(0.16, 0.14, 0.16, p.color(`${r}.1`), place(Math.cos(a) * 0.52, 0.22, Math.sin(a) * 0.5), { detail: 1 });
  }
  body.blob(0.22, 0.12, 0.44, p.color(`${r}.3`), place(-0.1, 1.3, 0), { detail: 2 });
  for (const z of [0.16, -0.16]) {
    body.cylinder(0.03, 0.02, 0.36, 5, p.color(`${r}.0`), new Matrix4().makeTranslation(0.05, 1.3, z)
      .multiply(new Matrix4().makeRotationZ(-0.35)));
    body.blob(0.07, 0.07, 0.07, p.color(`${r}.1`), place(0.18, 1.64, z), { detail: 1 });
  }
  body.blob(0.2, 0.22, 0.16, p.color('eye.1'), place(0.46, 0.94, 0.12), { detail: 2 });
  body.blob(0.07, 0.11, 0.07, p.color('eye.0'), place(0.62, 0.92, 0.12), { detail: 1 });
  body.blob(0.14, 0.12, 0.26, p.color('cheek.1'), place(0.56, 0.58, 0), { detail: 2 });
  pup.add('body', body, 0, 0.02, 0);
  feet(pup, p, 'bubTrim');
  return pup;
}

/** Drunk: a little man in a green hood, big round eyes, a pink nose, an orange bottle. */
export function buildDrunk(p: Palette, angry: boolean, material: Material): Puppet {
  const r = bodyRamp('drunk', angry);
  const pup = new Puppet(material);
  pup.turn = 0.7;
  const body = new MeshBuilder();
  body.blob(0.56, 0.6, 0.54, p.color(`${r}.2`), place(-0.04, 0.62, 0), { detail: 3 });
  body.blob(0.5, 0.48, 0.5, p.color(`${r}.2`), place(0.02, 1.34, 0), { detail: 3 });
  body.blob(0.16, 0.34, 0.16, p.color(`${r}.2`), new Matrix4().makeTranslation(-0.34, 1.72, 0)
    .multiply(new Matrix4().makeRotationZ(1.0)), { detail: 2 });
  body.blob(0.3, 0.3, 0.36, p.color('face.1'), place(0.34, 1.24, 0), { detail: 2 });
  eyes(body, p, 0.6, 1.36, 0.1, 0.24);
  body.blob(0.1, 0.08, 0.1, p.color('cheek.1'), place(0.64, 1.14, 0), { detail: 1 });
  pup.add('body', body, 0, 0.04, 0);
  const bottle = new MeshBuilder();
  bottle.cylinder(0.12, 0.12, 0.34, 10, p.color('fire.1'), place(0, 0, 0), p.color('fire.2'));
  bottle.cylinder(0.05, 0.05, 0.16, 8, p.color('fire.0'), place(0, 0.34, 0));
  pup.add('bottle', bottle, 0.5, 0.6, 0.46, 'body');
  feet(pup, p, 'hotFeet', 0.24, 0.22);
  return pup;
}

/** Banebou: an orange head with one big eye and pink cheeks, on a blue-and-white striped spring. */
export function buildBanebou(p: Palette, angry: boolean, material: Material): Puppet {
  const r = bodyRamp('banebou', angry);
  const pup = new Puppet(material);
  pup.turn = 0.7;
  const spring = new MeshBuilder();
  for (let i = 0; i < 5; i++) {
    spring.cylinder(0.24, 0.24, 0.1, 14, p.color(i % 2 ? 'paper.0' : 'shoe.1'), place(0, i * 0.1, 0));
  }
  pup.add('spring', spring, 0, 0, 0);
  const body = new MeshBuilder();
  body.blob(0.6, 0.54, 0.56, p.color(`${r}.2`), place(0, 0.46, 0), { detail: 3 });
  body.blob(0.2, 0.26, 0.16, p.color('eye.1'), place(0.4, 0.6, 0.14), { detail: 2 });
  body.blob(0.08, 0.13, 0.07, p.color('eye.0'), place(0.56, 0.58, 0.16), { detail: 1 });
  body.blob(0.13, 0.11, 0.07, p.color('cheek.1'), place(0.3, 0.34, 0.5), { detail: 2 });
  body.blob(0.13, 0.11, 0.07, p.color('cheek.1'), place(0.3, 0.34, -0.5), { detail: 2 });
  body.blob(0.12, 0.08, 0.14, p.color('bubTrim.1'), place(0.6, 0.36, 0), { detail: 1 });
  pup.add('body', body, 0, 0.5, 0);
  return pup;
}
